import type { CompiledElementStyles } from './compileElementStyles';
import { documentStyleHost, NO_STYLE_HOST, styleElement, type StyleSheetHost } from './styleSheetHost';

// One rule per distinct set of styles, and a count of how many elements still need it.
//
// This is the answer to the only hard question a hash-keyed stylesheet has: a merchant dragging the font
// size slider produces a new class on every frame, and a sheet that only ever grows would hold thousands
// of dead rules by lunchtime. So every rule is reference counted. An element takes a reference when it
// mounts and gives it back when it unmounts or its styles change; a rule nobody wears is not deleted at
// once — it goes on an idle list, because the very next keystroke often brings it straight back, and an
// undo always does. Only when the idle list has grown to twice its allowance is the sheet swept, dropping
// the oldest idle rules and rewriting what is left in one pass.
//
// The sheet is therefore bounded at the rules the page is actually wearing, plus a fixed slack, however
// long the merchant types. Sweeping in batches of a whole allowance keeps the rewrite amortised: no single
// release ever pays for more than a constant share of it.

/** Keeps one rule per class in a page, and hands back the release that lets go of it. */
export type StyleRegistry = {
  acquire(compiled: CompiledElementStyles): () => void;
  ruleCount(): number;
  idleCount(): number;
};

type Entry = { css: string; refs: number };

/** How many rules nothing wears are kept warm; the sheet is swept once twice this many have piled up. */
const IDLE_ALLOWANCE = 256;

/** One registry per document, so two element trees on one page share a single sheet and a single count. */
const REGISTRIES = new WeakMap<Document | ShadowRoot, StyleRegistry>();

// Keeps one rule per class, counts the elements wearing it, and sweeps the idle ones once too many pile up. Classes the
// page arrived with (a page drawn on the server) count as present, so taking the page over writes none of them again.
export function createStyleRegistry(host: StyleSheetHost, allowance: number = IDLE_ALLOWANCE, present: Iterable<string> = []): StyleRegistry {
  const entries = new Map<string, Entry>();
  for (const className of present) entries.set(className, { css: '', refs: 0 });
  let idle: string[] = [];

  // Drops the oldest rules nothing wears any more and rewrites the sheet, so typing cannot grow it for ever.
  const sweep = (): void => {
    const stale = idle.filter((className) => entries.get(className)?.refs === 0);
    const evicted = stale.slice(0, Math.max(0, stale.length - allowance));
    for (const className of evicted) entries.delete(className);
    idle = stale.slice(evicted.length);
    if (evicted.length > 0) host.reset([...entries.values()].map((entry) => entry.css).join(''));
  };

  // Lets go of one element's rule, keeping it warm in case the same styles come straight back.
  const release = (className: string): void => {
    const entry = entries.get(className);
    if (entry === undefined || entry.refs === 0) return;
    entry.refs -= 1;
    if (entry.refs > 0) return;
    idle.push(className);
    if (idle.length >= allowance * 2) sweep();
  };

  // Puts one element's rule in the page, or takes another reference on the rule already standing for it.
  const acquire = (compiled: CompiledElementStyles): (() => void) => {
    if (compiled.className === '') return () => {};
    const existing = entries.get(compiled.className);
    // A class the page arrived with learns its rule on first use, so a later sweep rewrites it rather than losing it.
    if (existing !== undefined) { existing.refs += 1; if (existing.css === '') existing.css = compiled.css; }
    else {
      entries.set(compiled.className, { css: compiled.css, refs: 1 });
      host.insert(compiled.css);
    }
    return () => release(compiled.className);
  };

  return { acquire, ruleCount: () => entries.size, idleCount: () => idle.length };
}

// Lists the element classes a page's sheet already holds — the ones a server drawing wrote — by the names in its rules.
export function presentClasses(css: string): Set<string> {
  return new Set(css.match(/\bxs-[a-z0-9]+\b/g) ?? []);
}

// Answers the one registry a document keeps, making it on first use so two trees on one page share a sheet.
export function getStyleRegistry(root: Document | ShadowRoot): StyleRegistry {
  const found = REGISTRIES.get(root);
  if (found !== undefined) return found;
  const created = createStyleRegistry(documentStyleHost(root), IDLE_ALLOWANCE, presentClasses(styleElement(root)?.textContent ?? ''));
  REGISTRIES.set(root, created);
  return created;
}

// Puts one compiled element's rules in a page and answers the release that takes them out again.
export function mountElementStyles(compiled: CompiledElementStyles, root?: Document | ShadowRoot): () => void {
  const target = root ?? (typeof document === 'undefined' ? null : document);
  if (target === null) return createStyleRegistry(NO_STYLE_HOST).acquire(compiled);
  return getStyleRegistry(target).acquire(compiled);
}

// Joins a page's compiled elements into one stylesheet, each rule once, for a page rendered without a browser.
export function renderElementStyleSheet(compiled: readonly CompiledElementStyles[]): string {
  const seen = new Set<string>();
  let css = '';
  for (const item of compiled) {
    if (item.className === '' || seen.has(item.className)) continue;
    seen.add(item.className);
    css += item.css;
  }
  return css;
}
